import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ExternalLink, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FormsList = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/cms/forms`);
      setForms(res.data);
    } catch { toast.error("Failed to load forms"); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createNew = async () => {
    const name = window.prompt("Form name (e.g. Newsletter, Contact Us):", "New Form");
    if (!name) return;
    try {
      const res = await axios.post(`${API}/cms/forms`, {
        name,
        fields: [
          { key: "name", label: "Name", type: "text", required: true },
          { key: "email", label: "Email", type: "email", required: true },
        ],
        submit_label: "Submit",
        success_message: "Thank you!",
      });
      nav(`/app/website/forms/${res.data.id}`);
    } catch { toast.error("Could not create form"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this form and all its submissions?")) return;
    await axios.delete(`${API}/cms/forms/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Forms</h1>
          <p className="text-sm text-slate-500">Build custom forms. Every submission can become a CRM lead.</p>
        </div>
        <button onClick={createNew} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-5 py-2.5 rounded-lg" data-testid="new-form-btn">
          <Plus size={16} /> New form
        </button>
      </header>

      {loading ? <div className="text-slate-400">Loading…</div> : forms.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          No forms yet. Click <b>New form</b> to create one.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Slug</th>
                <th className="text-left px-4 py-3 font-semibold">Fields</th>
                <th className="text-left px-4 py-3 font-semibold">Updated</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((f) => (
                <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3"><Link to={`/app/website/forms/${f.id}`} className="font-medium text-slate-900 hover:text-prepify-600 inline-flex items-center gap-2"><FileText size={14} className="text-slate-400" /> {f.name}</Link></td>
                  <td className="px-4 py-3 text-slate-500 text-xs font-mono">{f.slug}</td>
                  <td className="px-4 py-3 text-slate-500">{(f.fields || []).length}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(f.updated_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/app/website/forms/${f.id}/submissions`} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1"><ExternalLink size={12} /> Submissions</Link>
                      <Link to={`/app/website/forms/${f.id}`} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50"><Edit size={12} /></Link>
                      <button onClick={() => remove(f.id)} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FormsList;
